import React from 'react';
import { FiCamera, FiZap } from 'react-icons/fi';
import { useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { SCAN_MODE } from '@/lib/scanning/scanMode';
import './ScanModeSwitch.scss';

interface ScanModeSwitchProps {
  className?: string;
}

/**
 * Picks the reader. Present on every screen that scans, and switchable mid-task — an operator
 * who finds a label the handheld will not read needs the camera on the spot, without losing the
 * pick they are halfway through.
 *
 * A two-option segmented control rather than a toggle: with a toggle, the label has to describe
 * either the current state or the action, and whichever is chosen half the operators read it the
 * other way. Here both modes are visible and the selected one is obvious.
 */
const ScanModeSwitch = ({ className }: ScanModeSwitchProps) => {
  const { scanMode, setScanMode } = useScanner();

  const option = (mode: string, label: string, Icon: typeof FiCamera) => {
    const selected = scanMode === mode;
    return (
      <button
        type="button"
        className={`scan-mode-switch__option${
          selected ? ' scan-mode-switch__option--selected' : ''
        }`}
        aria-pressed={selected}
        title={label}
        onClick={() => !selected && setScanMode(mode)}
      >
        <Icon aria-hidden />
        {/* Hidden visually on small screens (icon only), but always the button's name. */}
        <span className="scan-mode-switch__label">{label}</span>
      </button>
    );
  };

  return (
    <div
      className={['scan-mode-switch', className].filter(Boolean).join(' ')}
      role="group"
      aria-label="Scanning mode"
    >
      {option(SCAN_MODE.CAMERA, 'Camera', FiCamera)}
      {option(SCAN_MODE.LASER, 'Laser', FiZap)}
    </div>
  );
};

export default ScanModeSwitch;
